// src/pages/AdminDashboard/AdminDashboard.jsx
import React, { useCallback, useEffect, useState } from "react";
import {
  ClipboardList,
  History,
  Inbox,
  LayoutDashboard,
  Megaphone,
  ShieldCheck,
  Users,
  UserPlus,
  GraduationCap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import api from "../../services/api";
import { ToastProvider, errorText } from "./components/common";
import OverviewPanel from "./components/OverviewPanel";
import UsersPanel from "./components/UsersPanel";
import QuizzesPanel from "./components/QuizzesPanel";
import AnnouncementsPanel from "./components/AnnouncementsPanel";
import MessagesPanel from "./components/MessagesPanel";
import AuditPanel from "./components/AuditPanel";
import AdminsPanel from "./components/AdminsPanel";
import "../../styles/dashboard-shell.css";
import "./AdminDashboard.css";
import "./AdminConsole.css";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard, blurb: "How MyEduGenius is being used right now." },
  { id: "users", label: "Users", icon: Users, blurb: "Find people, manage accounts, roles, verification and access." },
  { id: "quizzes", label: "Quizzes", icon: ClipboardList, blurb: "Review generated quizzes and remove ones that shouldn't be there." },
  { id: "announcements", label: "Announcements", icon: Megaphone, blurb: "Post notices to student and teacher dashboards." },
  { id: "messages", label: "Messages", icon: Inbox, blurb: "Messages sent through the Contact page." },
  { id: "admins", label: "Admins", icon: UserPlus, blurb: "Invite other administrators by email." },
  { id: "audit", label: "Activity log", icon: History, blurb: "A record of every change made by admins." },
];

// Which tab each quick action lands on
const ACTION_TAB = {
  "add-user": "users",
  "unverified-users": "users",
  "suspended-users": "users",
  "new-announcement": "announcements",
  "unhandled-messages": "messages",
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [intent, setIntent] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const name = localStorage.getItem("name") || sessionStorage.getItem("name") || "Admin";

  const loadStats = useCallback(async () => {
    setStatsError(null);
    try {
      const res = await api.get("/admin/stats");
      setStats(res.data);
    } catch (err) {
      setStatsError(errorText(err, "Couldn't load platform numbers."));
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const goTo = (id) => {
    setIntent(null);
    setTab(id);
  };

  const quickAction = (action) => {
    setIntent({ action, nonce: Date.now() });
    setTab(ACTION_TAB[action] || "overview");
  };

  const current = TABS.find((t) => t.id === tab);
  const unhandled = stats?.inbox?.unhandled_messages || 0;

  return (
    <Layout>
      <ToastProvider>
        <div className="student-dashboard admin-dashboard">
          <aside className="sidebar">
            <div className="sidebar-header">
              <div className="sidebar-header-top">
                <h2>Admin Console</h2>
              </div>
              <div className="student-info">
                <div className="student-avatar">
                  <ShieldCheck size={20} />
                </div>
                <div>
                  <h4>{name}</h4>
                  <p>Administrator</p>
                </div>
              </div>
            </div>
            <nav className="sidebar-nav" aria-label="Admin sections">
              {TABS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  className={`nav-item ${tab === id ? "active" : ""}`}
                  aria-current={tab === id ? "page" : undefined}
                  onClick={() => goTo(id)}
                >
                  <Icon size={20} />
                  <span>{label}</span>
                  {id === "messages" && unhandled > 0 && (
                    <span className="notification-badge admin-nav-badge" aria-label={`${unhandled} unhandled`}>
                      {unhandled > 99 ? "99+" : unhandled}
                    </span>
                  )}
                </button>
              ))}
              <button type="button" className="nav-item" onClick={() => navigate("/teacher-dashboard")}
                title="Open the teacher dashboard to test what teachers see">
                <GraduationCap size={20} />
                <span>Teacher view</span>
              </button>
            </nav>
          </aside>

          <main className="main-content">
            <div className="dashboard-content">
              <div className="welcome-section">
                <div className="welcome-text">
                  <h2>{current?.label}</h2>
                  <p>{current?.blurb}</p>
                </div>
              </div>

              {tab === "overview" && (
                <OverviewPanel
                  stats={stats}
                  statsError={statsError}
                  onRetryStats={loadStats}
                  onQuickAction={quickAction}
                />
              )}
              {tab === "users" && <UsersPanel intent={intent} onDataChanged={loadStats} />}
              {tab === "quizzes" && <QuizzesPanel />}
              {tab === "announcements" && <AnnouncementsPanel intent={intent} />}
              {tab === "messages" && <MessagesPanel intent={intent} onDataChanged={loadStats} />}
              {tab === "admins" && <AdminsPanel />}
              {tab === "audit" && <AuditPanel />}
            </div>
          </main>
        </div>
      </ToastProvider>
    </Layout>
  );
};

export default AdminDashboard;
